/**
 * ArbitrageLLMPanel — Opus 4.8's structured analysis.
 *
 * Surfaces the killer differentiation: Algorithm's recommendation (top)
 * vs. Opus's PRIMARY (which may differ) vs. Opus's WILDCARD alt.
 * Side-by-side framing makes the disagreement the headline feature.
 *
 * Loopholes are rendered as 3 cards with verbatim source quote + extraction
 * strategy. Adaptation spec is collapsible (long content, opt-in).
 */
import React, { useState } from "react";
import { ChevronDown, ChevronUp, Sparkles, Compass, MapPin, AlertCircle } from "lucide-react";

const FLAGS = { US: "🇺🇸", GB: "🇬🇧", IN: "🇮🇳", ES: "🇪🇸", FR: "🇫🇷", DE: "🇩🇪", CA: "🇨🇦", AU: "🇦🇺" };

const CountryBox = ({ label, code, reasoning, evidence, tone = "default", testId }) => {
  const tones = {
    algo: "border-zinc-200 bg-zinc-50",
    primary: "border-[#7C3AED] bg-[#7C3AED]/5",
    wildcard: "border-amber-400 bg-amber-50",
    default: "border-zinc-200 bg-white",
  };
  const labelTone = {
    algo: "text-zinc-500",
    primary: "text-[#7C3AED]",
    wildcard: "text-amber-700",
    default: "text-zinc-600",
  };
  return (
    <div data-testid={testId} className={`p-3 border ${tones[tone]} space-y-1.5`}>
      <div className="flex items-baseline justify-between">
        <span className={`text-[10px] font-black uppercase tracking-wider ${labelTone[tone]}`}>
          {label}
        </span>
        <div className="flex items-baseline gap-1">
          <span className="text-xl leading-none">{FLAGS[code] || "🌍"}</span>
          <span className="font-mono font-black text-base">{code || "—"}</span>
        </div>
      </div>
      {reasoning && (
        <p className="text-xs text-zinc-700 leading-snug">{reasoning}</p>
      )}
      {evidence && evidence.length > 0 && (
        <ul className="text-[11px] text-zinc-600 space-y-0.5 mt-1 pl-3 border-l border-zinc-200">
          {evidence.slice(0, 2).map((e, i) => (
            <li key={i} className="italic">&ldquo;{e}&rdquo;</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DisagreementHeader = ({ algoTop, sonnetPrimary, sonnetAlt }) => {
  const algoVsLlm = algoTop && sonnetPrimary && algoTop !== sonnetPrimary;
  return (
    <div className="flex items-center justify-between gap-3 p-2.5 border border-zinc-200 bg-gradient-to-r from-zinc-50 to-white">
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles size={14} className="text-[#7C3AED] shrink-0" />
        <span className="text-xs font-bold text-zinc-700 truncate">
          {algoVsLlm ? "Opus 4.8 disagrees with algorithm" : "Opus 4.8 endorses algorithm"}
        </span>
      </div>
      <div className="text-[10px] font-mono text-zinc-400 shrink-0">
        algo {algoTop} → llm {sonnetPrimary || "—"} · alt {sonnetAlt || "—"}
      </div>
    </div>
  );
};

const Loophole = ({ lh, idx }) => (
  <div data-testid={`loophole-card-${idx}`} className="p-3 border border-zinc-200 space-y-1.5 bg-white">
    <div className="flex items-start gap-2">
      <div className="w-5 h-5 bg-red-500 text-white font-black text-xs flex items-center justify-center shrink-0 mt-0.5">
        {idx + 1}
      </div>
      <p className="text-sm font-semibold text-zinc-900 leading-snug">{lh.gap}</p>
    </div>
    {lh.evidence_quote && (
      <blockquote className="text-xs text-zinc-700 italic border-l-2 border-zinc-300 pl-2 leading-snug">
        &ldquo;{lh.evidence_quote}&rdquo;
        <span className="text-[10px] not-italic text-zinc-400 ml-1">— {lh.evidence_source}</span>
      </blockquote>
    )}
    {lh.exploitation_strategy && (
      <div className="text-xs text-zinc-700 leading-snug bg-emerald-50 border border-emerald-200 p-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
          Exploit
        </span>
        <p className="mt-0.5">{lh.exploitation_strategy}</p>
      </div>
    )}
  </div>
);

const AdaptationSpec = ({ spec }) => {
  const [open, setOpen] = useState(false);
  if (!spec) return null;
  const stack = spec.tech_stack || {};
  return (
    <div data-testid="adaptation-spec" className="border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="adaptation-spec-toggle"
        className="w-full p-3 flex items-center justify-between hover:bg-zinc-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Compass size={14} className="text-[#7C3AED]" />
          <span className="text-sm font-bold">MVP Adaptation Spec</span>
          <span className="text-[10px] font-mono text-zinc-400 px-1.5 py-0.5 bg-zinc-100">
            {spec.confidence || "—"}
          </span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="p-3 pt-0 space-y-3 text-xs">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
              Target country
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-lg">{FLAGS[spec.target_country] || "🌍"}</span>
              <span className="font-mono font-bold">{spec.target_country || "—"}</span>
            </div>
          </div>
          {spec.mvp_features?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                MVP features
              </div>
              <ul className="space-y-0.5">
                {spec.mvp_features.map((f, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-[#7C3AED] font-bold">→</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(stack).map(([k, v]) => (
              <div key={k} className="p-2 bg-zinc-50 border border-zinc-100">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{k}</div>
                <div className="text-xs font-mono text-zinc-700 leading-snug">{v}</div>
              </div>
            ))}
          </div>
          {spec.regulatory_checklist?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                Regulatory checklist
              </div>
              <ul className="space-y-0.5">
                {spec.regulatory_checklist.map((r, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-amber-500 font-bold">▢</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-100">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Pricing</div>
              <div className="text-xs">{spec.pricing_model || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Budget</div>
              <div className="text-xs font-mono">
                ${spec.launch_budget_usd ? spec.launch_budget_usd.toLocaleString() : "—"}
              </div>
            </div>
          </div>
          {spec.gtm_channels?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                GTM channels
              </div>
              <div className="flex flex-wrap gap-1">
                {spec.gtm_channels.map((c, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-mono bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ArbitrageLLMPanel = ({ analysis, algoTopCountry, modelsUsed }) => {
  if (!analysis || Object.keys(analysis).length === 0) {
    return (
      <section data-testid="arbitrage-llm-panel" className="space-y-2">
        <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900">
          Arbitrage Analysis · LLM
        </h3>
        <div className="p-3 border border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
          No cached Opus 4.8 analysis for this opportunity yet.
        </div>
      </section>
    );
  }

  const arb = analysis.country_arbitrage || {};
  const cv = analysis.country_verification || {};
  const spec = analysis.adaptation_spec || {};
  const loopholes = analysis.loopholes || [];

  return (
    <section data-testid="arbitrage-llm-panel" className="space-y-3">
      <header className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900">
            Arbitrage Analysis · LLM
          </h3>
          <p className="text-xs text-zinc-500">
            Opus 4.8 reasoning over signals, comps, and verbatim user complaints.
          </p>
        </div>
      </header>

      {/* The Disagreement Header — the killer feature */}
      <DisagreementHeader
        algoTop={algoTopCountry}
        sonnetPrimary={arb.primary_recommendation}
        sonnetAlt={arb.alternative_recommendation}
      />

      {/* 3-way country comparison */}
      <div className="space-y-2">
        <CountryBox
          label="Algorithm recommends"
          code={algoTopCountry}
          reasoning="Z-scored weighted signal aggregate — quantitative only."
          tone="algo"
          testId="country-box-algo"
        />
        <CountryBox
          label="Opus 4.8 primary"
          code={arb.primary_recommendation}
          reasoning={arb.primary_reasoning}
          tone="primary"
          testId="country-box-primary"
        />
        {arb.alternative_recommendation && (
          <CountryBox
            label="Opus 4.8 wildcard alternative"
            code={arb.alternative_recommendation}
            reasoning={arb.alternative_reasoning}
            evidence={arb.alternative_evidence}
            tone="wildcard"
            testId="country-box-wildcard"
          />
        )}
      </div>

      {/* Loopholes */}
      {loopholes.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MapPin size={12} className="text-red-500" />
            <span className="text-[10px] font-black uppercase tracking-wider text-zinc-700">
              {loopholes.length} verified loophole{loopholes.length === 1 ? "" : "s"}
            </span>
            <span className="text-[10px] font-mono text-zinc-400">
              · all quotes verbatim from G2 / Reddit
            </span>
          </div>
          {loopholes.map((lh, i) => (
            <Loophole key={i} lh={lh} idx={i} />
          ))}
        </div>
      )}

      {/* Adaptation spec */}
      <AdaptationSpec spec={spec} />

      {/* Confidence note + model attribution */}
      {analysis.confidence_notes && (
        <div className="flex gap-2 p-2.5 bg-zinc-50 border border-zinc-200">
          <AlertCircle size={14} className="text-zinc-500 shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-700 leading-snug">{analysis.confidence_notes}</p>
        </div>
      )}
      <p className="text-[10px] font-mono text-zinc-400 text-right">
        {modelsUsed?.analyzer || "claude-opus-4-8"} · loopholes verbatim-validated
      </p>
    </section>
  );
};

export default ArbitrageLLMPanel;
